// Pure transition function for attachment re-download records.
//
// Two entry points:
//   - `transition(record, outcome, now)` — applies an outcome to an
//     `inFlight` record and returns the next snapshot. Idempotent for
//     terminal records (`expired` and `completed`) so a hostile network
//     cannot undo a verdict by re-delivering a stale result.
//   - `startAttempt(record, now)` — flips a `pending` or `backoff` record
//     to `inFlight` and stamps `lastAttemptedAt`. The caller is responsible
//     for issuing the network request out-of-band.
//
// The "backoff → inFlight" promotion is the caller's job — the scheduler
// scans `dueForRetry()` and calls `startAttempt` before firing the network
// task. We keep `transition` outcome-driven only.
//
// Time is supplied by the caller so the self-test can pin timestamps
// reproducibly without touching real clocks.
//
// Web mirror of kaether_for_ios/Stoat/E2EE/AttachmentRedownloadStateMachine.swift

import type {
  AttachmentRedownloadOutcome,
  AttachmentRedownloadRecord,
} from "./AttachmentRedownload";
import {
  DISTANT_FUTURE,
  MAX_ATTEMPTS,
  nextEligibleAt,
} from "./AttachmentRedownloadPolicy";

/**
 * Applies a single download outcome to an `inFlight` record and returns the
 * next snapshot.
 *
 * Returns the record **unchanged** if it is already terminal (`expired` /
 * `completed`) — those verdicts are monotonic to defend against replayed
 * network responses.
 *
 * Also returns the record unchanged if it is not in `inFlight` state (a
 * stale outcome arrived for a record that has since been re-issued).
 *
 * @param record   Current immutable snapshot.
 * @param outcome  Network outcome to apply.
 * @param now      Unix timestamp (ms) at which the outcome was observed.
 */
export function transition(
  record: AttachmentRedownloadRecord,
  outcome: AttachmentRedownloadOutcome,
  now: number,
): AttachmentRedownloadRecord {
  // Terminal states are sticky: a late `.success` arriving after we've
  // already moved on, or a 410 arriving after we already expired, cannot
  // perturb the record.
  if (record.state === "expired" || record.state === "completed") {
    return record;
  }
  // We only act on `inFlight` records — anything else is a stale outcome
  // (caller fired one earlier and the record has since been re-issued) and
  // is ignored.
  if (record.state !== "inFlight") {
    return record;
  }

  if (outcome.kind === "success") {
    return {
      ...record,
      state: "completed",
      lastAttemptedAt: now,
      nextEligibleAt: DISTANT_FUTURE,
    };
  }

  if (outcome.kind === "permanentFailure" && outcome.httpStatus === 410) {
    // Server has confirmed the blob is gone. Jump straight to `expired`
    // regardless of the attempt counter.
    return {
      ...record,
      state: "expired",
      attemptCount: record.attemptCount + 1,
      lastAttemptedAt: now,
      nextEligibleAt: DISTANT_FUTURE,
      failureHistory: [...record.failureHistory, 410],
    };
  }

  if (outcome.kind === "permanentFailure") {
    // Server-side error (404, 5xx, etc.). Advance the counter and either
    // schedule the next backoff slot, or — if we've burned the retry budget —
    // flip to `expired`.
    const nextAttempts = record.attemptCount + 1;
    const history = [...record.failureHistory, outcome.httpStatus];
    if (nextAttempts >= MAX_ATTEMPTS) {
      return {
        ...record,
        state: "expired",
        attemptCount: nextAttempts,
        lastAttemptedAt: now,
        nextEligibleAt: DISTANT_FUTURE,
        failureHistory: history,
      };
    }
    return {
      ...record,
      state: "backoff",
      attemptCount: nextAttempts,
      lastAttemptedAt: now,
      nextEligibleAt: nextEligibleAt(nextAttempts, now),
      failureHistory: history,
    };
  }

  // transientFailure — network blip (DNS / timeout / connection reset). Do
  // not advance the attempt counter; reschedule on a short fixed 4-second
  // wait so the caller retries soon without burning the budget.
  return {
    ...record,
    state: "backoff",
    // attemptCount is intentionally UNCHANGED
    lastAttemptedAt: now,
    nextEligibleAt: now + 4_000,
    // failureHistory is intentionally UNCHANGED
  };
}

/**
 * Flips a record to `inFlight` and stamps `lastAttemptedAt`.
 *
 * Idempotent for already-terminal records (returns them unchanged). The
 * caller is responsible for kicking off the actual network task.
 *
 * @param record  Current immutable snapshot.
 * @param now     Unix timestamp (ms) at which the attempt begins.
 */
export function startAttempt(
  record: AttachmentRedownloadRecord,
  now: number,
): AttachmentRedownloadRecord {
  if (record.state === "expired" || record.state === "completed") {
    return record;
  }
  return {
    ...record,
    state: "inFlight",
    lastAttemptedAt: now,
  };
}
